/**
 * useBulkOperations
 *
 * Shared bulk-operation state and handlers for list/index routes: selection
 * set, bulk delete/update dialog + busy + result state, and the
 * delete/update/export handlers. The entity-specific REST calls are injected
 * via `bulkDelete`/`bulkUpdate` and the GraphQL queries to refetch via
 * `refetchQueries`, so a single hook drives every entity list.
 */

import { useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { exportToCSV, exportToJSON } from '../lib/exportUtils';
import type { BulkOperationResult } from '../lib/apiClient';

export interface UseBulkOperationsOptions<T, U> {
  /** Filtered rows currently in view — used for selected-name lookup and export. */
  rows: T[];
  getRowId: (row: T) => string;
  getRowName: (row: T) => string;
  /** REST bulk-delete call for the entity. */
  bulkDelete: (ids: string[]) => Promise<BulkOperationResult>;
  /** REST bulk-update call for the entity. */
  bulkUpdate: (ids: string[], updates: U) => Promise<unknown>;
  /** GraphQL query names to refetch after a successful mutation (e.g. ['ListServices']). */
  refetchQueries: string[];
  /** Base filename (without extension) for CSV/JSON export of the selection. */
  exportFileName: string;
}

export interface UseBulkOperationsResult<U> {
  selectedIds: Set<string>;
  setSelectedIds: (ids: Set<string>) => void;
  clearSelection: () => void;
  selectedNames: string[];
  bulkDeleteOpen: boolean;
  setBulkDeleteOpen: (open: boolean) => void;
  bulkUpdateOpen: boolean;
  setBulkUpdateOpen: (open: boolean) => void;
  isBulkDeleting: boolean;
  isBulkUpdating: boolean;
  bulkDeleteResult: BulkOperationResult | null;
  handleBulkDelete: () => Promise<void>;
  handleBulkDeleteDone: () => void;
  handleBulkUpdate: (updates: U) => Promise<void>;
  handleBulkExport: (format: 'csv' | 'json') => void;
}

export function useBulkOperations<T, U>({
  rows,
  getRowId,
  getRowName,
  bulkDelete,
  bulkUpdate,
  refetchQueries,
  exportFileName,
}: UseBulkOperationsOptions<T, U>): UseBulkOperationsResult<U> {
  const apolloClient = useApolloClient();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkDeleteResult, setBulkDeleteResult] = useState<BulkOperationResult | null>(null);

  const clearSelection = (): void => setSelectedIds(new Set());

  const selectedNames = rows.filter((row) => selectedIds.has(getRowId(row))).map(getRowName);

  const handleBulkDelete = async (): Promise<void> => {
    setIsBulkDeleting(true);
    try {
      const result = await bulkDelete(Array.from(selectedIds));
      setBulkDeleteResult(result);
      if (result.succeeded.length > 0) {
        await apolloClient.refetchQueries({ include: refetchQueries });
      }
    } catch {
      setBulkDeleteResult({
        succeeded: [],
        failed: Array.from(selectedIds).map((id) => ({ id, error: 'Request failed' })),
      });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkDeleteDone = (): void => {
    setBulkDeleteOpen(false);
    setBulkDeleteResult(null);
    setSelectedIds(new Set());
  };

  const handleBulkUpdate = async (updates: U): Promise<void> => {
    setIsBulkUpdating(true);
    try {
      await bulkUpdate(Array.from(selectedIds), updates);
      await apolloClient.refetchQueries({ include: refetchQueries });
      setBulkUpdateOpen(false);
      setSelectedIds(new Set());
    } catch {
      // Error surfaced via the future notification system; dialog stays open for retry.
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkExport = (format: 'csv' | 'json'): void => {
    const selectedRows = rows.filter((row) => selectedIds.has(getRowId(row)));
    if (format === 'csv') {
      exportToCSV(selectedRows as object[] as Record<string, unknown>[], exportFileName);
    } else {
      exportToJSON(selectedRows, exportFileName);
    }
  };

  return {
    selectedIds,
    setSelectedIds,
    clearSelection,
    selectedNames,
    bulkDeleteOpen,
    setBulkDeleteOpen,
    bulkUpdateOpen,
    setBulkUpdateOpen,
    isBulkDeleting,
    isBulkUpdating,
    bulkDeleteResult,
    handleBulkDelete,
    handleBulkDeleteDone,
    handleBulkUpdate,
    handleBulkExport,
  };
}
